export type ImageCrop = {
	imageScale: number
	imageOffsetX: number
	imageOffsetY: number
}

/** Below this the image stops covering its frame and letterboxes. */
export const IMAGE_CROP_SCALE_MIN = 100

/**
 * A sanity ceiling, not a quality one — at any usable master a crop this deep is
 * already a mistake. It exists so a stray scroll cannot write a value the renderers
 * disagree about; the form viewer clamps to the same number.
 */
export const IMAGE_CROP_SCALE_MAX = 1000

export const imageCropDefault: ImageCrop = {
	imageScale: IMAGE_CROP_SCALE_MIN,
	imageOffsetX: 50,
	imageOffsetY: 50,
}

export type ImageCropBoxStyle = {
	widthPercent: number
	heightPercent: number
	leftPercent: number
	topPercent: number
}

/**
 * Percentages of the frame, so the result is resolution-independent: the 500px editor
 * preview and the full-width public page resolve to the same crop. At scale 100 this
 * reproduces `object-fit: cover`, and the offsets match `object-position` percentages.
 */
export function resolveImageCropBox(
	crop: ImageCrop,
	naturalWidth: number,
	naturalHeight: number,
	frameAspectRatio: number,
): ImageCropBoxStyle | null {
	if (naturalWidth <= 0 || naturalHeight <= 0 || frameAspectRatio <= 0) {
		return null
	}

	const scale = crop.imageScale / 100
	const imageAspectRatio = naturalWidth / naturalHeight

	// Cover is driven by whichever axis runs short: a frame-wider image fills the height
	// and overflows sideways, a frame-taller one fills the width and overflows down.
	const coverWidthPercent = imageAspectRatio >= frameAspectRatio ? (imageAspectRatio / frameAspectRatio) * 100 : 100
	const coverHeightPercent = imageAspectRatio >= frameAspectRatio ? 100 : (frameAspectRatio / imageAspectRatio) * 100

	const widthPercent = coverWidthPercent * scale
	const heightPercent = coverHeightPercent * scale

	return {
		widthPercent,
		heightPercent,
		leftPercent: (100 - widthPercent) * (clampOffset(crop.imageOffsetX) / 100),
		topPercent: (100 - heightPercent) * (clampOffset(crop.imageOffsetY) / 100),
	}
}

function clampOffset(offset: number): number {
	return Math.min(Math.max(offset, 0), 100)
}

/**
 * Source pixels available for each pixel the crop is displayed at. Below 1 the image
 * is being enlarged past its own resolution, which no amount of CDN sizing can fix —
 * the detail is not in the file.
 */
export function croppedPixelDensity(
	crop: ImageCrop,
	naturalWidth: number,
	naturalHeight: number,
	frameAspectRatio: number,
	displayedWidthPx: number,
): number | null {
	const box = resolveImageCropBox(crop, naturalWidth, naturalHeight, frameAspectRatio)
	if (!box || displayedWidthPx <= 0) {
		return null
	}

	return (naturalWidth * (100 / box.widthPercent)) / displayedWidthPx
}
